import { apiErrorSchema } from '@noconflict/contracts';
import type { z } from 'zod';
import { env } from '../config/env';
import { i18n } from '../i18n';
import type errors from '../i18n/locales/en-US/errors.json';

type ApiErrorCode = Exclude<keyof typeof errors.api, 'generic'>;

const knownCodes = new Set<string>([
  'bad_request',
  'unauthorized',
  'forbidden',
  'not_found',
  'run_id_taken',
  'run_active',
  'invitation_email_failed',
  'billing_profile_sync_failed',
  'payments_not_configured',
  'order_required',
  'version_not_runnable',
  'internal',
] satisfies ApiErrorCode[]);

/** The worker answers with error codes; the text a user sees comes from the catalogue. */
export function apiErrorMessage(code: string): string {
  return knownCodes.has(code)
    ? i18n.t(`errors:api.${code as ApiErrorCode}`)
    : i18n.t('errors:api.generic');
}

/**
 * Error thrown for any non-2xx worker response. `message` is the localised text for `code`;
 * the worker's own prose is kept as `serverMessage` for logs only.
 */
export class ApiRequestError extends Error {
  constructor(
    public readonly code: string,
    public readonly serverMessage: string,
    public readonly status: number,
    public readonly correlationId: string | null,
  ) {
    super(apiErrorMessage(code));
    this.name = 'ApiRequestError';
  }
}

/** Fetches a worker endpoint with a correlation id, a bearer token when given, and typed parsing. */
export async function apiFetch<T>(
  path: string,
  schema: z.ZodType<T>,
  init: RequestInit & { token?: string } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('x-correlation-id', crypto.randomUUID());
  headers.set('accept', 'application/json');
  if (init.token) headers.set('authorization', `Bearer ${init.token}`);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  const res = await fetch(`${env.apiBase}${path}`, { ...init, headers });
  const correlationId = res.headers.get('x-correlation-id');
  if (!res.ok) {
    const parsed = apiErrorSchema.safeParse(await res.json().catch(() => null));
    const err = parsed.success
      ? parsed.data.error
      : { code: 'unknown', message: `HTTP ${res.status}` };
    throw new ApiRequestError(err.code, err.message, res.status, correlationId);
  }
  // 204: nothing to parse (a delete)
  return schema.parse(res.status === 204 ? undefined : await res.json());
}
